class ShowroomSetup extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 0;
    this.steps = Array.from(this.querySelectorAll('.showroom-setup__card'));
    this.totalSteps = this.steps.length;

    this.btnBack = this.querySelector('[showroom-back]');
    this.btnNext = this.querySelector('[showroom-next]');
    this.btnSave = this.querySelector('[showroom-save]');
    this.btnClose = this.querySelectorAll('[showroom-close]');
    this.loading = this.querySelector('[showroom-final-step-loading]');
    this.ready = this.querySelector('[showroom-final-step-ready]');
    this.sizeInput = this.querySelector('input[name="showroom[size]"]');
    this.limitSize = Number(this.dataset.limitSize) || 13;
    this.minimumDaysForDate = Number(this.dataset.minimumDays) || 30;
    this.dateInputBar = document.querySelector('#clickable_date_container');

    this.customer = {
      name: this.dataset.customerName,
      email: this.dataset.customerEmail
    }

    this.buttons = [this.btnBack, this.btnNext, this.btnSave];

    this.config = {
      role: null,
      size: parseInt(this.sizeInput?.value, 10),
      name_bride: '',
      name_partner: '',
      date: '',
      no_date: false,
      colors: [],
      no_color: false,
      setup_reason: '',
    };

    this.setupListeners();
  }

  async connectedCallback() {
    if (!this.customer.name || !this.customer.email) {
      console.error('Customer name and email are required for showroom setup.');
      const headerBtn = document.querySelectorAll('[trigger-create-showroom]');
      headerBtn.forEach(btn => {
        btn.disabled = true
        btn.classList.add('btn--disabled')
      });
      return
    }

    this.updateStep();
    document.addEventListener('toggleShowroom', this.toggleShowroom.bind(this));
    document.addEventListener('openCreateShowroom', this.open.bind(this));
    this.btnClose.forEach(btn => {
      btn?.addEventListener('click', this.close.bind(this));
    });
    window.openCreateShowroom = this.open.bind(this);

    document.body.addEventListener('click', (e) => {
      const btn = e.target.closest('[trigger-create-showroom]');
      const menu = document.getElementById('mobile-menu');
      const overlay = document.getElementById('menu-overlay');
      if (btn) {
        menu.classList.add('-translate-x-full');
        overlay.classList.add('pointer-events-none');
        overlay.classList.remove('opacity-100');
        document.body.classList.remove('overflow-hidden');

        this.handleOpen();
      }
    });

    this.initDatePicker();
    if (this.sizeInput) {
      this.sizeInput.addEventListener('input', () => {
        let value = parseInt(this.sizeInput.value, 10);

        if (isNaN(value)) {
          this.sizeInput.value = '';
          return;
        }

        if (value > this.limitSize) {
          this.sizeInput.value = this.limitSize;
        } else if (value < 1) {
          this.sizeInput.value = 1;
        }
      });

      this.sizeInput.addEventListener('keydown', (e) => {
        if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
          e.preventDefault();
        }
      });
    }
    await this.waitForResolvedColors();
    this.renderAllColors();
  }

  waitForResolvedColors() {
    return new Promise(resolve => {
      const check = () => {
        if (window.__VPV?.resolvedShowroomColors) resolve();
        else setTimeout(check, 50);
      };
      check();
    });
  }

  initDatePicker() {
    const dateInput = this.querySelector('#wedding_date');
    if (!dateInput) return;

    // Compute limits
    const today = new Date();
    today.setHours(0, 0, 0, 0);
     const minDate = new Date(today.getTime() + this.minimumDaysForDate * 24 * 60 * 60 * 1000);
    const maxDate = new Date(today.getFullYear() + 20, 11, 31);

    // Convert ISO string (YYYY-MM-DD) to Date object
    let defaultDate = null;
    if (dateInput.value) {
      const parts = dateInput.value.split('-'); // ["2026","08","17"]
      if (parts.length === 3) {
        defaultDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      }
    }

    flatpickr(dateInput, {
      dateFormat: "m/d/Y",  // MM/DD/YYYY
      allowInput: true,
      minDate: minDate,
      maxDate: maxDate,
      defaultDate,
      clickOpens: true
    });
  }

  disconnectedCallback() {
    document.removeEventListener('toggleShowroom', this.toggleShowroom.bind(this));
    document.removeEventListener('openCreateShowroom', this.open.bind(this));
  }

  toggleShowroom() {
    this.classList.toggle('is-open');
  }

  handleOpen() {
    this.open();
    const openEvent = new CustomEvent('openModal', {
      detail: { modal: this }
    });
    window.dispatchEvent(openEvent);
  }

  open() {
    this.classList.add('is-open');
  }

  close() {
    this.classList.remove('is-open');
  }

  setupListeners() {
    const { btnBack, btnNext, btnSave } = this;

    this.querySelector('.showroom-setup__close button')?.addEventListener('click', this.toggleShowroom.bind(this));
    this.querySelector('.showroom-setup__slider')?.addEventListener('scroll', this.handleScroll.bind(this));

    btnNext?.addEventListener('click', this.handleNextStep.bind(this));
    btnBack?.addEventListener('click', this.handleBackStep.bind(this));
    btnSave?.addEventListener('click', this.handleSubmit.bind(this));

    this.querySelectorAll('.showroom-setup__single__role')?.forEach((button, index) => {
      button?.addEventListener('click', () => this.handleRoleSelection(button, index));
    });

    this.querySelector('[data-action="decrement"]')?.addEventListener('click', this.decrementSize.bind(this));
    this.querySelector('[data-action="increment"]')?.addEventListener('click', this.incrementSize.bind(this));

    const dateInput = document.getElementById('wedding_date');
    dateInput.addEventListener('input', function(e) {
      let v = e.target.value.replace(/\D/g, '').slice(0,8);
      if (v.length >= 5) v = v.replace(/(\d{2})(\d{2})(\d{1,4})/, '$1/$2/$3');
      else if (v.length >= 3) v = v.replace(/(\d{2})(\d{1,2})/, '$1/$2');
      e.target.value = v;
    });

    this.setupInputListeners();
    this.updateCardState();
    this.setupColorSelection();

    this.querySelectorAll('input[name="showroom[setup_reason]"]').forEach(radio => {
      radio.addEventListener('change', (e) => {
        this.config.setup = e.target.value;
        this.updateCardState();
      });
    });
  }

  setupInputListeners() {
    const inputs = [
      { name: 'showroom[size]', handler: 'size' },
      { name: 'showroom[bride_name]', handler: 'name_bride' },
      { name: 'showroom[partner_name]', handler: 'name_partner' },
      { name: 'showroom[date]', handler: 'date' },
      { name: 'showroom[color]', handler: 'colors' },
      { name: 'showroom[setup_reason]', handler: 'setup_reason' }
    ];

    inputs?.forEach(({ name, handler }) => {
      this.querySelector(`input[name="${name}"]`)
        ?.addEventListener('input', (e) => {
          this.config[handler] = e.target.value;
          this.updateCardState();
        });
    });

    const noSizeCheckbox = this.querySelector('input[name="showroom[no-size]"]');
    const noSizeCheckboxBox = this.querySelector('#showroom-checkbox-size-not-finalized + label');

    const noDateCheckbox = this.querySelector('input[name="showroom[no-date]"]');
    const noDateCheckboxBox = this.querySelector('#showroom-checkbox-date-not-finalized + label');

    const noColorCheckbox = this.querySelector('input[name="showroom[no-color]"]');
    const noColorCheckboxBox = this.querySelector('#showroom-checkbox-color-not-finalized + label');

    this.setupCheckboxListeners(noSizeCheckbox, noSizeCheckboxBox, 'no_size');
    this.setupCheckboxListeners(noDateCheckbox, noDateCheckboxBox, 'no_date');
    this.setupCheckboxListeners(noColorCheckbox, noColorCheckboxBox, 'no_color');
  }

  setupCheckboxListeners(checkbox, checkboxBox, namespace) {
    checkboxBox?.addEventListener('click', (e) => {
      e.preventDefault();
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event('change'));
    });

    checkboxBox?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change'));
      }
    });

    checkbox?.addEventListener('change', () => {
      this.config[namespace] = checkbox.checked;
      this.updateCardState();

      if (namespace === 'no_date' && checkbox.checked) {
        this.nextStep();
      }
      if (namespace === 'no_color' && checkbox.checked) {
        this.nextStep();
      }
    });
  }

  renderAllColors() {
    const container = this.querySelector('#swatches-container');
    if (!container || !window.__VPV?.resolvedShowroomColors) return;

    container.innerHTML = ''; // Clear previous colors

    window.__VPV.resolvedShowroomColors.forEach(color => {
      const button = document.createElement('button');
      button.className = 'swatch_button';
      button.setAttribute('role', 'listitem');
      button.dataset.colorReference = color.reference;
      button.dataset.colorName = color.label;
      button.dataset.colorImage = color.image.url;
      button.dataset.value = color.label.replace(/\s+/g, '-').toLowerCase();
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('name', 'showroom[colors]');

      // If color is already selected, highlight it
      const isSelected = this.config.colors.some(c => c.name === color.label);
      if (isSelected) {
        button.classList.add('ring-black', 'disabled');
      }

      // Create image element
      const img = document.createElement('img');
      img.src = color.image.url;
      img.alt = color.label;
      img.className = 'w-full h-full object-cover rounded-full';

      button.appendChild(img);
      container.appendChild(button);
    });

    // Re-attach click listeners
    this.setupColorSelection();
  }

  setupColorSelection() {
    const swatches = this.querySelectorAll('[data-color-name]');
    const selectedContainer = this.querySelector('#selected-colors-container');

    // Ensure config.colors is always an array
    this.config.colors = this.config.colors || [];

    const renderSelectedColors = () => {
      selectedContainer.innerHTML = '';

      this.config.colors.forEach(colorObj => {
        const colorButton = Array.from(swatches).find(btn => btn.dataset.colorName === colorObj.name);
        if (!colorButton) return;

        const colorImage = colorObj.image;
        const colorName = colorObj.name;
        const colorReference = colorObj.reference;

        const colorEl = document.createElement('div');
        colorEl.className = 'relative flex color_item';

        const img = document.createElement('img');
        img.src = colorImage;
        img.alt = colorName;
        img.className = 'w-[32px] h-[32px] rounded-full object-cover flex-shrink-0';

        const label = document.createElement('span');
        label.textContent = colorName;
        label.setAttribute('data-color-reference', colorReference);

        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'color-item-remove';
        removeBtn.setAttribute('aria-label', `Remove ${colorName}`);
        removeBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
            <path d="M17.7609 17.1908C17.6726 17.1908 17.5844 17.1601 17.5129 17.0977C17.3556 16.9608 17.3387 16.7218 17.4757 16.5638C18.938 14.8835 19.7433 12.7294 19.7433 10.4991C19.7433 7.82372 18.5848 5.28109 16.5647 3.52334C16.4067 3.38642 16.3905 3.14741 16.5274 2.98947C16.665 2.83153 16.904 2.81532 17.0613 2.95224C19.2466 4.85471 20.4999 7.60513 20.4999 10.4997C20.4999 12.9126 19.6286 15.2427 18.0468 17.0611C17.9717 17.1469 17.8666 17.1908 17.7609 17.1908Z" fill="#D0B2A9"/>
            <path d="M10.4999 20.4999C8.08694 20.4999 5.75689 19.6286 3.93849 18.0468C3.78115 17.9099 3.76433 17.6708 3.90125 17.5129C4.03817 17.355 4.27718 17.3387 4.43512 17.4757C6.1154 18.938 8.2695 19.7433 10.4999 19.7433C13.1752 19.7433 15.7179 18.5848 17.4756 16.5647C17.6131 16.4067 17.8515 16.3905 18.0095 16.5274C18.1674 16.6644 18.1836 16.9034 18.0467 17.0613C16.1448 19.2466 13.3944 20.4999 10.4999 20.4999Z" fill="#D0B2A9"/>
            <path d="M4.18664 18.1398C4.09837 18.1398 4.01009 18.1092 3.93863 18.0467C1.7533 16.1448 0.5 13.3944 0.5 10.4999C0.5 8.08694 1.37137 5.75689 2.95316 3.93849C3.09008 3.78115 3.32909 3.76433 3.48703 3.90125C3.64497 4.03817 3.66118 4.27718 3.52426 4.43512C2.06197 6.1154 1.25667 8.2695 1.25667 10.4999C1.25667 13.1752 2.41509 15.7179 4.43526 17.4756C4.5932 17.6125 4.60942 17.8515 4.4725 18.0095C4.39743 18.0953 4.29234 18.1398 4.18664 18.1398Z" fill="#D0B2A9"/>
            <path d="M3.23847 4.56558C3.15019 4.56558 3.06191 4.53495 2.99045 4.4725C2.83251 4.33558 2.81629 4.09657 2.95321 3.93863C4.85509 1.7533 7.60551 0.5 10.5001 0.5C12.913 0.5 15.243 1.37137 17.0614 2.95316C17.2188 3.09008 17.2356 3.32909 17.0987 3.48703C16.9617 3.64497 16.7227 3.66118 16.5648 3.52426C14.8845 2.06197 12.7304 1.25667 10.5001 1.25667C7.8247 1.25667 5.28206 2.41509 3.52432 4.43526C3.44925 4.52114 3.34416 4.56558 3.23847 4.56558Z" fill="#D0B2A9"/>
            <path d="M13.4043 13.8337C13.3324 13.8337 13.26 13.807 13.2033 13.7534C13.1838 13.735 13.1643 13.7156 13.1453 13.6963L7.28397 7.75515C7.27271 7.74374 7.26145 7.73188 7.25019 7.72003C7.13671 7.60148 7.13974 7.41226 7.25668 7.29722C7.37362 7.18219 7.56029 7.18527 7.67377 7.30381C7.68286 7.31347 7.69196 7.32269 7.70149 7.33235L13.5623 13.2735C13.5766 13.288 13.5909 13.3025 13.6057 13.3161C13.7248 13.4289 13.7313 13.6177 13.62 13.7385C13.5619 13.8017 13.4831 13.8337 13.4043 13.8337Z" fill="#222222"/>
            <path d="M7.55731 13.8293C7.48021 13.8293 7.40312 13.799 7.34551 13.7384C7.23247 13.6198 7.2355 13.4306 7.35244 13.3156C7.36197 13.3064 7.37107 13.2971 7.3806 13.2875L10.3307 10.2969L13.2809 7.30636C13.2956 7.29143 13.3103 7.27606 13.3246 7.26069C13.4364 7.14083 13.623 7.13513 13.7413 7.2484C13.8595 7.36167 13.8651 7.5509 13.7534 7.67076C13.7352 7.69008 13.7166 7.7094 13.698 7.72872L7.79725 13.7107C7.78556 13.7221 7.77386 13.734 7.7626 13.7454C7.705 13.8016 7.63137 13.8293 7.55731 13.8293Z" fill="#222222"/>
          </svg>
        `;

        removeBtn.addEventListener('click', () => {
          this.config.colors = this.config.colors.filter(c => c.name !== colorObj.name);

          // Remove highlight & disabled state from swatch
          colorButton.classList.remove('ring-black', 'disabled');
          colorButton.disabled = false;
          colorButton.setAttribute('aria-pressed', 'false');

          // Re-render updated list
          renderSelectedColors();
          this.updateCardState();
        });

        colorEl.appendChild(img);
        colorEl.appendChild(label);
        colorEl.appendChild(removeBtn);
        selectedContainer.appendChild(colorEl);
      });

      selectedContainer.classList.toggle('hidden', this.config.colors.length === 0);
    };

    swatches.forEach(button => {
      button.addEventListener('click', () => {
        if (button.disabled || button.classList.contains('disabled')) return;

        const colorName = button.dataset.colorName;
        const colorImage = button.dataset.colorImage;
        const colorReference = button.dataset.colorReference;

        const alreadySelected = this.config.colors.some(
          c => c.name === colorName
        );
        if (alreadySelected) return;

        this.config.colors.push({
          name: colorName,
          image: colorImage,
          reference: colorReference
        });

        // Add highlight & disabled style
        button.classList.add('ring-black', 'disabled');
        button.disabled = true;
        button.setAttribute('aria-pressed', 'true');

        renderSelectedColors();
        this.updateCardState();
      });
    });
  }

  incrementSize() {
    let currentValue = parseInt(this.sizeInput.value, 10);
    if (currentValue < this.limitSize) {
      currentValue++;
      this.sizeInput.value = currentValue;
      this.config.size = currentValue;
      this.updateCardState();
    }
  }

  decrementSize() {
    let currentValue = parseInt(this.sizeInput.value, 10);
    if (currentValue > 1) {
      currentValue--;
      this.sizeInput.value = currentValue;
      this.config.size = currentValue;
      this.updateCardState();
    }
  }

  handleRoleSelection(button, index) {
    if (this.config.role === this.getRoleName(index)) {
      this.config.role = null;
      button.classList.remove('is-active');
    } else {
      this.config.role = this.getRoleName(index);
      this.querySelectorAll('.showroom-setup__single__role').forEach(btn => btn.classList.remove('is-active'));
      button.classList.add('is-active');
    }
    this.updateCardState();
  }

  handleScroll() {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      const nearestStep = this.getNearestStep();
      if (nearestStep !== this.currentStep && this.validateCurrentStep()) {
        this.currentStep = nearestStep;
        this.updateStep();
      } else {
        this.scrollToStep(this.currentStep);
      }
    }, 100);
  }

  getNearestStep() {
    const slider = this.querySelector('.showroom-setup__slider');
    let nearestIndex = 0;
    let minDistance = Infinity;

    this.steps.forEach((step, index) => {
      const distance = Math.abs(slider.scrollLeft - step.offsetLeft);
      if (distance < minDistance) {
        minDistance = distance;
        nearestIndex = index;
      }
    });

    return Math.min(nearestIndex, this.totalSteps - 1);
  }

  handleNextStep() {
    this.nextStep();
  }

  handleBackStep() {
    if (this.currentStep > 0) {
      this.currentStep--;
      this.scrollToStep(this.currentStep);
    }
  }

  nextStep() {
    if (this.currentStep < this.totalSteps - 1) {
      this.currentStep++;
      this.scrollToStep(this.currentStep);
    }
  }

  scrollToStep(stepIndex) {
    if (stepIndex >= 0 && stepIndex < this.totalSteps) {
      const step = this.steps[stepIndex];
      step.scrollIntoView({ behavior: 'smooth' });
      this.currentStep = stepIndex;
      this.updateStep();
    }
  }

  updateStep() {
    const navTemplate = this.querySelector('.showroom-setup__nav').getAttribute('data-lang-nav-template');
    this.querySelector('.showroom-setup__nav').textContent = navTemplate
      .replace('{current}', this.currentStep + 1)
      .replace('{total}', this.totalSteps);
    this.updateProgress();
    this.updateButtonVisibility();
  }

  updateCardState() {
    const step = this.steps[this.currentStep];
    if (!step) { return; }

    if (this.validateCurrentStep()) {
      step.classList.add('is-ready');
    } else {
      step.classList.remove('is-ready');
    }
    this.updateButtonVisibility();
  }

  updateButtonVisibility() {
    const { btnBack, btnNext, btnSave, currentStep } = this;
    const step = this.steps[currentStep];

    const isFinalMessage = step.classList.contains('showroom-setup__final');
    if (isFinalMessage) {
      [btnBack, btnNext, btnSave].forEach(btn => this.toggleButtonVisibility(btn, false));
      return;
    }

    this.toggleButtonVisibility(btnBack, currentStep > 0);
    this.toggleButtonVisibility(btnNext, currentStep < this.totalSteps - 1 && step.classList.contains('is-ready'));
    this.toggleButtonVisibility(btnSave, currentStep === this.totalSteps - 2 && step.classList.contains('is-ready'));

    this.animateButtons();
  }

  toggleButtonVisibility(button, visible) {
    if (visible) {
      button.classList.add('is-visible');
      button.disabled = false;
      button.tabIndex = 0;
    } else {
      button.classList.remove('is-visible');
      button.disabled = true;
      button.tabIndex = -1;
    }
  }

  animateButtons() {
    const gap = 8;
    const visibleButtons = this.buttons.filter(button => button.classList.contains('is-visible'));
    if (visibleButtons.length === 0) { return; }

    if (visibleButtons.length > 1) {
      visibleButtons.forEach((button, index) => {
        button.style.marginLeft = index === 0
          ? `-${(button.clientWidth) + gap}px`
          : `${(button.clientWidth) + gap}px`;
      });
    } else {
      visibleButtons[0].style.marginLeft = 0;
    }
  }

  updateProgress() {
    const progress = ((this.currentStep + 1) / this.totalSteps) * 100;
    this.querySelector('.showroom-setup__progressbar__bar').style.width = `${progress}%`;
  }

  validateCurrentStep() {
    const step = this.steps[this.currentStep];
    if (!step) { return false; }

    if (step.classList.contains('showroom-setup__role') && !this.config.role) return false;
    if (step.classList.contains('showroom-setup__names')) return this.config.name_bride && this.config.name_partner;
    if (step.classList.contains('showroom-setup__date')) return this.config.no_date || this.validateDate();
    if (step.classList.contains('showroom-setup__size')) return this.config.size || this.config.no_size;
    if (step.classList.contains('showroom-setup__colors')) return this.config.colors.length > 0 || this.config.no_color;
    if (step.classList.contains('showroom-setup__setup_reason')) return !!this.config.setup;

    return true;
  }

  validateDate() {
    const dateInput = this.config.date;
    if (this.config.no_date || !dateInput) {
      return true;
    }

    const selectedDate = new Date(dateInput);
    const today = new Date();
    today.setHours(0, 0, 0, 0); 

    const diffInTime = selectedDate.getTime() - today.getTime();
    const diffInDays = diffInTime / (1000 * 3600 * 24);

    if (selectedDate <= today) {
      return false;
    }

    if (selectedDate.getFullYear() - today.getFullYear() > 20) {
      return false;
    }

    if (diffInDays < this.minimumDaysForDate) {
      return false;
    }

    return true;
  }

  handleSubmit() {
    if (this.validateCurrentStep()) {
      const showroomData = {
        bride: this.config.name_bride || '',
        partner: this.config.name_partner || '',   
        creatorName: this.customer.name || '',
        creatorEmail: encodeURIComponent(this.customer.email || ''),
        creatorRole: this.config.role || ''
      }

      if (this.config.no_date) {
        showroomData.date_not_finalized_yet = 'true';
        showroomData.date = '';
      } else {
        const parts = this.config.date.split('/');
        if (parts.length === 3) {
          const [month, day, year] = parts;
          const isoDate = new Date(`${year}-${month}-${day}T00:00:00Z`).toISOString();
          showroomData.date = isoDate;
        } else {
          showroomData.date = null; // fallback if parsing fails
        }
        showroomData.date_not_finalized_yet = 'false';
      }
      if (this.config.no_size) {
        showroomData.size_not_finalized_yet = 'true';
        showroomData.size = '';
      } else {
        showroomData.size = this.config.size.toString();
        showroomData.size_not_finalized_yet = 'false';
      }

      if (this.config.no_color) {
        showroomData.colors = []; 
        showroomData.color_not_finalized_yet = 'true';
      } else if (this.config.colors && this.config.colors.length > 0) {
        const colorGIDs = this.config.colors
          .map(c => c?.reference)
          .filter(Boolean);
        showroomData.colors = colorGIDs;
        showroomData.color_not_finalized_yet = 'false';
      } else {
        showroomData.colors = [];
        showroomData.color_not_finalized_yet = 'false';
      }

      if (this.config.setup) {
        showroomData.setup_reason = this.config.setup;
      } 

      this.handleCreateAndAnimation(showroomData);
    } else {
      alert('Please fill in all required fields.');
    }
  }

  async handleCreateAndAnimation(data) {
    this.currentStep++;
    this.scrollToStep(this.currentStep);

    const step = this.steps[this.currentStep];
    step.scrollIntoView({ behavior: 'smooth' });
    this.buttons.forEach(button => this.toggleButtonVisibility(button, false));
    document.querySelector(".showroom-setup__footer__ctas").style.opacity = '0';
    document.querySelector(".showroom-setup__nav").style.display = 'none';

    try {
      setTimeout(() => {
        this.loading.classList.add('fade-up-enter-active');
      }, 700);

      const response = await fetch(`${window.SHOWROOM.api}/createShowroom`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'email': encodeURIComponent(this.customer.email),

        },
        body: JSON.stringify(data),
      });
      console.log("Submitting showroomData", data);
      const responseData = await response.json();
      console.log("Raw API response", responseData);
      const handle = responseData.handle;

      if (!handle) {
        throw new Error('Error with API');
      }
      
      setTimeout(() => {
        this.loading.classList.remove('fade-up-enter-active');
        setTimeout(() => {
          this.loading.classList.add('hidden');
          this.ready.classList.remove('hidden');

          setTimeout(() => {
          this.ready.classList.add('fade-up-enter-active');

            setTimeout(() => {
              window.location = `/pages/showroom?id=${handle}`;
            }, 2000);

          }, 500);

        }, 500);

      }, 2000); // Fake loading time
    } catch (error) {
      this.currentStep--;
      this.scrollToStep(this.currentStep);

      this.loading.classList.add('fade-up-enter-active');
      this.ready.classList.add('hidden');
      
      console.error('Error creating showroom:', error);
      alert('There was an error creating the showroom. Please try again.');
    }
  }

  getRoleName(index) {
    return this.querySelectorAll('.showroom-setup__single__role')[index].dataset.value;
  }

  toggleDateInput(enable) {
    const dateInput = document.getElementById('wedding_date');
    if (enable) {
      dateInput.removeAttribute('disabled');
    } else {
      dateInput.setAttribute('disabled', 'disabled');
      dateInput.value = '';
      if (this.config) {
        this.config.date = '';
      }
    }
  }
}

customElements.define('showroom-setup', ShowroomSetup);